/**
 * INTAKE-V2-BUILD-V1 -- the consent PENDING PHOTO slice (C14, founder review
 * 2026-07-31 item 6; brief 8.4 + frames A4 / A6 / B-PAPER / S8).
 *
 * PAPER CONSENT IS PHOTOGRAPHED: consent may be printed and signed; the
 * receptionist then captures a photo. Until it is attached the record
 * carries a LOUD pending state with EXACTLY ONE action, `Take photo`. The
 * badge and the action are IDENTICAL on every surface; only the short
 * context line varies (the four chartered variants below -- pinned).
 *
 * THE STORAGE SEAM, stated precisely: NO binary-document owner exists in
 * the repo today (brief 8.4), and schema introspection for this build
 * confirms NO consent-photo object exists in database/schema_master.sql.
 * Migrations are FILE-ONLY for agents and nothing here may mint one, so
 * this slice ships the STATE and the ACTION with a typed capture SEAM:
 * `ConsentPhotoCaptureSeam` is the ONE interface the shared proof-photo
 * capture component (chartered to PROOF-EVIDENCE-SPINE -- brief section
 * 16.1: ONE component for reception consent / doctor case-sheet photos /
 * accounts vouchers, NEVER duplicated here) will implement. Until that
 * carrier lands, pressing `Take photo` opens the HONEST slot: the capture
 * position is drawn, visibly marked as later work, and no claim of storage
 * is ever made (the existing shipped honesty-line posture). Nothing here
 * reads, parses, or extracts an image (brief 16.2).
 */

import { AcButton } from '@/dashboard/ui';

// ---- Copy (pinned) ---------------------------------------------------------

export const PENDING_PHOTO_BADGE = 'Pending photo';
export const TAKE_PHOTO_ACTION = 'Take photo';

/** A4 -- the consent step, the moment paper is chosen. */
export const PENDING_PHOTO_LINE_CONSENT_STEP =
  'Consent signed on paper is not recorded until photographed.';
/** A6 -- the success screen (the badge + one word; the block stays loudest). */
export const PENDING_PHOTO_LINE_SUCCESS = 'Consent';
/** B-PAPER -- the intake paper path. */
export const PENDING_PHOTO_LINE_PAPER_FORM = 'Not recorded until photographed';
/** S8 -- the record-level state, wherever the patient appears. */
export const PENDING_PHOTO_LINE_RECORD = 'Consent signed on paper';

/** The capture slot's honest later-work line: the storage mechanism ships
 *  with the chartered carrier, and this surface never claims a copy was
 *  stored (reuses the shipped honesty posture verbatim -- IntakeDrawer's
 *  document-honesty line is the precedent copy). */
export const CONSENT_PHOTO_SLOT_HONESTY =
  'The signed original stays filed at the desk. Photo capture and storage arrive with the shared proof-photo package - nothing is stored yet.';
export const CONSENT_PHOTO_SLOT_TITLE = 'Attach signed copy';

// ---- The derived state (pure) ----------------------------------------------

export type ConsentPhotoState = 'none' | 'pending_photo';

/**
 * Session-derived pending state: TRUE exactly when the paper path carries
 * the registration consent (C14). A property of the RECORD in doctrine; the
 * wire-level record derivation ships with the storage carrier -- this pure
 * rule is the ONE client derivation both wizards use meanwhile.
 */
export function deriveConsentPhotoState(
  consentPath: '' | 'digital' | 'paper',
): ConsentPhotoState {
  return consentPath === 'paper' ? 'pending_photo' : 'none';
}

// ---- The capture seam ------------------------------------------------------

/** The ONE interface the shared PROOF-EVIDENCE-SPINE capture component will
 *  implement. subject: what the photo proves; onAttached: fired ONLY when a
 *  photo is durably stored (never fired by the interim slot). */
export interface ConsentPhotoCaptureSeam {
  readonly subject: 'registration_consent' | 'intake_consent';
  readonly onAttached?: (evidenceRef: string) => void;
}

// ---- The loud block (one badge, one line, one action) ----------------------

export interface PendingPhotoBlockProps {
  /** The per-surface context line (one of the four pinned variants). */
  readonly line: string;
  /** Opens the capture seam; the host owns placement of the slot. */
  readonly onTakePhoto: () => void;
  readonly disabled?: boolean;
  readonly testId?: string;
}

/** The PENDING PHOTO block: red-bordered, loud, exactly one action. */
export function PendingPhotoBlock({
  line,
  onTakePhoto,
  disabled,
  testId,
}: PendingPhotoBlockProps): JSX.Element {
  return (
    <div className="ac-pending-photo" data-testid={testId ?? 'pending-photo-block'}>
      <span className="ac-pending-photo__badge">{PENDING_PHOTO_BADGE}</span>
      <span className="ac-pending-photo__line">{line}</span>
      <AcButton
        size="sm"
        variant="primary"
        disabled={disabled}
        onClick={onTakePhoto}
        data-testid="pending-photo-take"
      >
        {TAKE_PHOTO_ACTION}
      </AcButton>
    </div>
  );
}

/** The interim capture slot (brief A4: "an Attach signed copy slot rendered
 *  in its future position, visibly marked as later work"). Renders the seam
 *  position honestly; stores nothing; never fires onAttached. */
export function ConsentPhotoCaptureSlot({
  seam,
  onClose,
}: {
  readonly seam: ConsentPhotoCaptureSeam;
  readonly onClose: () => void;
}): JSX.Element {
  return (
    <div
      className="ac-consent-photo-slot"
      data-testid="consent-photo-capture-slot"
      data-subject={seam.subject}
    >
      <p className="ac-consent-photo-slot__title">{CONSENT_PHOTO_SLOT_TITLE}</p>
      <p className="ac-consent-photo-slot__honesty">{CONSENT_PHOTO_SLOT_HONESTY}</p>
      <AcButton
        size="sm"
        variant="secondary"
        onClick={onClose}
        data-testid="consent-photo-slot-close"
      >
        Close
      </AcButton>
    </div>
  );
}
